import dayjs, { Dayjs } from 'dayjs'
import { birthDayOf } from '../../../utils/birthDay'

/** How many birthdays the "upcoming" strip shows by default. */
export const UPCOMING_BIRTHDAYS_LIMIT = 8

interface DatedBirthday {
  /** ISO date; read with birthDayOf. */
  birthDate: string
}

/**
 * Returns the next `limit` birthdays ordered by their next occurrence from
 * `now`, ignoring the birth year. A birthday whose day/month has already passed
 * this year rolls over to next year, so it lands at the end of the list.
 *
 * Pure and order-stable: the input array is copied (never mutated) and ties on
 * `daysAway` keep the caller's original order.
 */
export function computeUpcomingBirthdays<T extends DatedBirthday>(
  birthdays: T[],
  now: Dayjs = dayjs().startOf('day'),
  limit: number = UPCOMING_BIRTHDAYS_LIMIT,
): T[] {
  const reference = now.startOf('day')
  return [...birthdays]
    .map((b) => {
      const bd = dayjs(birthDayOf(b.birthDate), 'YYYY-MM-DD')
      let next = bd.year(reference.year())
      // From the birth date, not from this year's day: a 29 February shown on
      // the 28th this year is back on the 29th when next year has one.
      if (next.isBefore(reference, 'day')) next = bd.year(reference.year() + 1)
      return { b, daysAway: next.diff(reference, 'day') }
    })
    .sort((a, z) => a.daysAway - z.daysAway)
    .slice(0, limit)
    .map((x) => x.b)
}
